import React, { FC } from "react";
import { Col, Form, Select, Input } from "antd";
import {
	distributionPlatformFilterOptions,
	seriesFilterOptions,
	tagsFilterOptions,
	videoListfilterOptions,
} from "../../../utils";
import { BorderedRow, CloseIcon } from "../..";
import { LanguageData } from "../../../videos/modals/AddVideosMetaDataForm";
import { TagOption } from "../../../tags/hooks/utils";

const { Item } = Form;

interface CommonItemProps {
	style?: object;
	name?: string;
	label?: string;
	placeholder?: string;
	mode?: "multiple" | "tags";
	disabled?: boolean;
}

interface VideoListFilterFormItemProp extends CommonItemProps {
	videoLists: { id: string; name: string }[];
}

export const VideoListSelectItem: FC<VideoListFilterFormItemProp> = ({
	videoLists,
	name,
	label,
	placeholder,
	mode,
}) => {
	return (
		<Item label={label ?? "Video Lists"} name={name ?? "video_lists"}>
			<Select
				filterOption={videoListfilterOptions}
				mode={mode}
				allowClear={{ clearIcon: <CloseIcon /> }}
				showSearch
				placeholder={placeholder ?? "Video Lists"}
			>
				{videoLists?.map((videoList: any) => (
					<Select.Option value={videoList.id} key={videoList.id}>
						{videoList.name}
					</Select.Option>
				))}
			</Select>
		</Item>
	);
};

interface SeriesFormItemProps extends CommonItemProps {
	seriesData: any[];
}

export const SeriesSelectItem: FC<SeriesFormItemProps> = ({
	style,
	seriesData,
	name,
	label,
	placeholder,
	mode,
}) => {
	return (
		<Item name={name ?? "series"} label={label ?? "Series"} style={style}>
			<Select
				filterOption={seriesFilterOptions}
				showSearch
				mode={mode}
				placeholder={placeholder ?? "Series"}
				allowClear={{ clearIcon: <CloseIcon /> }}
			>
				{seriesData
					.sort((a, b) => a.titles.localeCompare(b.titles))
					.map((series: any) => (
						<Select.Option value={series.id} key={series.id}>
							{series.titles}
						</Select.Option>
					))}
			</Select>
		</Item>
	);
};

export const EpisodeRelatedFiltersForAssets: FC<{
	seriesData: any[];
	videoLists: any[];
	distributionPlatformData?: any[];
}> = ({ videoLists, seriesData, distributionPlatformData }) => {
	return (
		<BorderedRow>
			{distributionPlatformData && (
				<Col span={24}>
					<DistributionPlatformsItem
						distributionPlatformData={distributionPlatformData}
						mode="multiple"
						name="distribution_platforms_filter"
					/>
				</Col>
			)}
			<Col span={24}>
				<SeriesSelectItem name="series_filter" seriesData={seriesData} mode="multiple" />
			</Col>
			<Col span={24}>
				<VideoListSelectItem name="video_lists_filter" videoLists={videoLists} mode="multiple" />
			</Col>
			<Col span={24}>
				<Item name="status_filter" label="Episode Status">
					<Input placeholder="i.e. active, inactive, or restricted etc..." />
				</Item>
			</Col>
			<Col span={24}></Col>
		</BorderedRow>
	);
};

interface DistributionPlatformFilterFormItemProps extends CommonItemProps {
	distributionPlatformData: any[];
}

export const DistributionPlatformsItem: FC<DistributionPlatformFilterFormItemProps> = ({
	distributionPlatformData,
	name,
	label,
	placeholder,
	mode,
}) => {
	return (
		<Item name={name ?? "distribution_platforms"} label={label ?? "Distribution Platforms"}>
			<Select
				mode={mode}
				allowClear={{ clearIcon: <CloseIcon /> }}
				placeholder={placeholder ?? "Distribution Platforms"}
				filterOption={distributionPlatformFilterOptions}
			>
				{distributionPlatformData?.map((platform: any) => (
					<Select.Option value={platform.id} key={platform.id}>
						{platform.name}
					</Select.Option>
				))}
			</Select>
		</Item>
	);
};

interface TagsFilterFormItemProps extends CommonItemProps {
	tagsData: any[];
	for_thumbnails?: boolean;
}

export const TagsFilterItem: FC<TagsFilterFormItemProps> = ({
	tagsData,
	name,
	label,
	placeholder,
	mode,
	for_thumbnails,
}) => {
	tagsData = tagsData.filter((tag: TagOption) =>
		tag.related_to.includes(for_thumbnails ? "thumbnail" : "video")
	);
	return (
		<Item name={name ?? "tags"} label={label ?? "Tags"}>
			<Select
				mode={mode}
				allowClear={{ clearIcon: <CloseIcon /> }}
				placeholder={placeholder ?? "Tags"}
				filterOption={tagsFilterOptions}
			>
				{tagsData.map((tag: TagOption) => (
					<Select.Option value={tag.id} key={tag.id}>
						{tag.title}
					</Select.Option>
				))}
			</Select>
		</Item>
	);
};

interface LanguageFilterFormItemProps extends CommonItemProps {
	languages: any[];
}

export const LanguageSelectItem: FC<LanguageFilterFormItemProps> = ({
	style,
	languages,
	name,
	label,
	placeholder,
	mode,
	disabled,
}) => {
	return (
		<Item name={name ?? "languages"} label={label ?? "Languages"} style={style}>
			<Select
				disabled={disabled}
				allowClear={{ clearIcon: <CloseIcon /> }}
				mode={mode}
				placeholder={placeholder ?? "Language"}
			>
				{languages?.map((language: LanguageData) => (
					<Select.Option value={language.NAME} key={language.ID}>
						{language.NAME}
					</Select.Option>
				))}
			</Select>
		</Item>
	);
};

export const CaptionTypeFilterItem: FC<CommonItemProps> = ({
	name,
	label,
	placeholder,
	mode,
	disabled,
}) => {
	return (
		<Item name={name ?? "caption_type_filter"} label={label ?? "Caption Type"}>
			<Select
				disabled={disabled}
				allowClear={{ clearIcon: <CloseIcon /> }}
				mode={mode}
				placeholder={placeholder ?? "Caption Type"}
			>
				<Select.Option value="scc">SCC</Select.Option>
				<Select.Option value="vtt">VTT</Select.Option>
				<Select.Option value="srt">SRT</Select.Option>
			</Select>
		</Item>
	);
};
